import { useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

/**
 * Hook for persisting the DataGrid search query in the URL.
 *
 * Returns:
 * - `initialSearchValue`: the search term restored from `?search=` on mount
 * - `updateSearchInUrl`: call to sync the current search term into the URL
 * - `navigateWithSearch`: navigate to a path while carrying the `?search=` param
 * - `searchQuery`: the `?search=…` query string (or empty), for building route strings
 */
export const useSearchPersist = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialSearchValue = searchParams.get('search') || '';

  const updateSearchInUrl = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (searchValue) {
        newParams.set('search', searchValue);
      } else {
        newParams.delete('search');
      }
      navigate({ search: newParams.toString() }, { replace: true });
    },
    [searchParams, navigate],
  );

  const navigateWithSearch = useCallback(
    (path: string) => {
      const searchValue = searchParams.get('search');
      const query = searchValue
        ? `?search=${encodeURIComponent(searchValue)}`
        : '';
      navigate(`${path}${query}`);
    },
    [navigate, searchParams],
  );

  const searchQuery = initialSearchValue
    ? `?search=${encodeURIComponent(initialSearchValue)}`
    : '';

  return {
    initialSearchValue,
    updateSearchInUrl,
    navigateWithSearch,
    searchQuery,
  };
};
